import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Box, { BoxProps } from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import MuiDialogActions from "@mui/material/DialogActions";
import Stack, { StackProps } from "@mui/material/Stack";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import MuiSwipeableDrawer from "@mui/material/SwipeableDrawer";

import { useIsScrolling } from "./_useIsScrolling";
import { StorybookDialogPreview } from "./StorybookDialogPreview";

import { mergeSx } from "@/common/helpers/mergeSx";
import { mergeRefs } from "@/common/helpers/refs";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import {
  ButtonVariant,
  ButtonVibe,
  DashboardIconButton,
  DashboardIconButtonProps,
} from "@/dashboard-design-system/DashboardButton";
import { StorybookMobileDialogDrawerPreview } from "@/dashboard-design-system/DashboardDialog/StorybookMobileDialogDrawerPreview";
import { DashboardIconBlock, DashboardIconBlockProps } from "@/dashboard-design-system/DashboardIconBlock";
import { DashboardTypography, DashboardTypographyProps } from "@/dashboard-design-system/Typography";
import { Success, Cross, Info, Warning } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

export type DashboardDialogVibe = "brand" | "warning" | "danger" | "positive";

export type DashboardDialogProps = {
  open?: boolean;
  onClose?: () => void;
  vibe?: DashboardDialogVibe;
  children: ReactNode;
  inline?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg";
  mobileBehaviour?: "drawer" | "dialog";
  sx?: SxProps<Theme>;
};

const DashboardDialogContext = createContext<{
  container: "dialog" | "drawer" | undefined;
  vibe: DashboardDialogVibe;
  onClose?: DashboardDialogProps["onClose"];
  dialogMode: "default" | "overflow";
  onChangeDialogMode?: (dialogMode: "default" | "overflow") => void;
}>({ vibe: "brand", container: undefined, dialogMode: "default" });

export const DashboardDialog = forwardRef<HTMLDivElement, DashboardDialogProps>(
  ({ children, mobileBehaviour, sx, ...props }, ref) => {
    const { isPhoneScreen } = useMediaQuery();

    if (isPhoneScreen && mobileBehaviour === "drawer") {
      return (
        <DashboardMobileDialogDrawer {...props} sx={sx} ref={ref}>
          {children}
        </DashboardMobileDialogDrawer>
      );
    }

    return (
      <DesktopDashboardDialog {...props} sx={sx} ref={ref}>
        {children}
      </DesktopDashboardDialog>
    );
  }
);

DashboardDialog.displayName = "DashboardDialog";

type DesktopDashboardDialogProps = Omit<DashboardDialogProps, "mobileBehaviour">;

export const DesktopDashboardDialog = forwardRef<HTMLDivElement, DesktopDashboardDialogProps>(
  ({ open = false, onClose, children, inline = false, vibe = "brand", maxWidth = "xs", sx }, ref) => {
    const [dialogMode, setDialogMode] = useState<"default" | "overflow">("default");

    useEffect(() => {
      if (!open) {
        // Reset dialog mode when dialog is closed
        setDialogMode("default");
      }
    }, [open]);

    const renderDialog = () => {
      if (inline) {
        return (
          <StorybookDialogPreview ref={ref} maxWidth={maxWidth}>
            {children}
          </StorybookDialogPreview>
        );
      }

      return (
        <MuiDialog
          ref={ref}
          open={open}
          onClose={onClose}
          maxWidth={maxWidth}
          scroll="paper"
          sx={sx}
          PaperProps={{ sx: { width: "100%" } }}
        >
          {children}
        </MuiDialog>
      );
    };

    return (
      <DashboardDialogContext.Provider
        value={{ vibe, onClose, container: "dialog", dialogMode, onChangeDialogMode: setDialogMode }}
      >
        {renderDialog()}
      </DashboardDialogContext.Provider>
    );
  }
);

DesktopDashboardDialog.displayName = "DesktopDashboardDialog";

type DashboardMobileDialogDrawerProps = Omit<DashboardDialogProps, "maxWidth">;

export const DashboardMobileDialogDrawer = forwardRef<HTMLDivElement, DashboardMobileDialogDrawerProps>(
  ({ open = false, onClose, children, inline = false, vibe = "brand", sx }, ref) => {
    const [dialogMode, setDialogMode] = useState<"default" | "overflow">("default");

    useEffect(() => {
      if (!open) {
        // Reset dialog mode when dialog is closed
        setDialogMode("default");
      }
    }, [open]);

    const renderDialog = () => {
      if (inline) {
        return (
          <StorybookMobileDialogDrawerPreview ref={ref} dialogMode={dialogMode}>
            {children}
          </StorybookMobileDialogDrawerPreview>
        );
      }

      return (
        <MuiSwipeableDrawer
          ref={ref}
          open={open}
          onOpen={() => {}}
          onClose={onClose ?? (() => {})}
          anchor="bottom"
          sx={sx}
          PaperProps={{ sx: { borderRadius: dialogMode === "default" ? "20px 20px 0 0" : undefined } }}
        >
          {children}
        </MuiSwipeableDrawer>
      );
    };

    return (
      <DashboardDialogContext.Provider
        value={{ vibe, onClose, container: "drawer", dialogMode, onChangeDialogMode: setDialogMode }}
      >
        {renderDialog()}
      </DashboardDialogContext.Provider>
    );
  }
);

DashboardMobileDialogDrawer.displayName = "DashboardMobileDialogDrawer";

type DashboardDialogBodyProps = {
  slots: {
    header?: ReactNode;
    content: ReactNode;
    footer?: ReactNode;
  };
};

export const DashboardDialogBody = forwardRef<HTMLDivElement, DashboardDialogBodyProps>(({ slots }, ref) => {
  return (
    <Stack ref={ref} sx={{ minHeight: 0, borderRadius: "inherit" }}>
      {slots.header}
      {slots.content}
      {slots.footer}
    </Stack>
  );
});

DashboardDialogBody.displayName = "DashboardDialogBody";

type DashboardDialogHeaderProps = Omit<StackProps, "children"> & {
  icon?: IconElement;
  title?: ReactNode;
  slotProps?: {
    title?: Omit<DashboardTypographyProps, "ref">;
    icon?: Omit<DashboardIconBlockProps, "ref" | "children">;
    closeButton?: Omit<DashboardIconButtonProps, "ref" | "children" | "onClick">;
  };
};

export const DashboardDialogHeader = forwardRef<HTMLDivElement, DashboardDialogHeaderProps>(
  ({ icon: iconProp, title, slotProps, sx }, ref) => {
    const { vibe: inheritedVibe, onClose, dialogMode } = useContext(DashboardDialogContext);
    const theme = useTheme();

    const vibe = slotProps?.icon?.vibe || inheritedVibe;

    const icon = useMemo(() => {
      if (iconProp !== undefined) {
        return iconProp;
      }

      switch (vibe) {
        case "warning":
          return <Warning />;
        case "positive":
          return <Success />;
        default:
          return <Info />;
      }
    }, [vibe, iconProp]);

    const styles = mergeSx(
      {
        padding: theme.spacing(3, 3, 1, 3),
        gap: 2,
        borderStartStartRadius: "inherit",
        borderStartEndRadius: "inherit",
        ...(dialogMode === "overflow" && {
          borderBlockEnd: `1px solid ${theme.palette.border.neutral.quiet}`,
        }),
      },
      sx
    );

    return (
      <Stack ref={ref} sx={styles}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <DashboardIconBlock size="large" vibe={vibe} {...slotProps?.icon}>
            {icon}
          </DashboardIconBlock>
          <DashboardIconButton vibe="neutral" onClick={onClose} {...slotProps?.closeButton}>
            <Cross />
          </DashboardIconButton>
        </Stack>
        {title && (
          <DashboardTypography variant="h6" vibe="text-neutral-intense" {...slotProps?.title}>
            {title}
          </DashboardTypography>
        )}
      </Stack>
    );
  }
);

DashboardDialogHeader.displayName = "DashboardDialogHeader";

type DashboardDialogContentProps = BoxProps;

export const DashboardDialogContent = forwardRef<HTMLDivElement, DashboardDialogContentProps>(
  ({ children, sx, ...rest }, ref) => {
    const theme = useTheme();
    const { onChangeDialogMode } = useContext(DashboardDialogContext);

    const internalRef = useRef<HTMLDivElement | null>(null);

    useIsScrolling({
      ref: internalRef,
      callback: useCallback(
        (isScrolling: { y: boolean }) => {
          onChangeDialogMode?.(isScrolling.y ? "overflow" : "default");
        },
        [onChangeDialogMode]
      ),
    });

    const styles = mergeSx(
      {
        flex: 1,
        padding: theme.spacing(1, 3, 1, 3),
        ...theme.typography.body2,
        overflowY: "auto",
        scrollbarColor: `${theme.palette.surface.brand.moderate} transparent`,
        ["&::-webkit-scrollbar-track"]: {
          background: "transparent",
        },
        ["&::-webkit-scrollbar-thumb"]: {
          background: theme.palette.surface.brand.moderate,
        },
      },
      sx
    );

    return (
      <Box ref={mergeRefs(ref, internalRef)} sx={styles} {...rest}>
        {children}
      </Box>
    );
  }
);

DashboardDialogContent.displayName = "DashboardDialogContent";

type DashboardDialogFooterProps = StackProps & {
  align?: "start" | "center";
};

type MainButtonProps = {
  variant: ButtonVariant;
  vibe: ButtonVibe;
  type: "submit" | "button";
};

type SecondaryButtonProps = {
  variant: ButtonVariant;
  vibe: ButtonVibe;
};

export const DashboardDialogFooter = forwardRef<HTMLDivElement, DashboardDialogFooterProps>(
  ({ children, align = "start", sx }, ref) => {
    const { vibe, dialogMode, container } = useContext(DashboardDialogContext);
    const theme = useTheme();
    const childrenArray = Children.toArray(children);

    const mainButton = childrenArray[0] as ReactElement<MainButtonProps>;
    const secondaryButton = childrenArray[1] as ReactElement<SecondaryButtonProps>;

    const styles = mergeSx(
      {
        display: "flex",
        flexDirection: { xs: "column", md: container === "dialog" ? "row-reverse" : undefined },
        justifyContent: { md: container === "dialog" ? (align === "start" ? "flex-start" : "center") : undefined },
        width: "100%",
        alignItems: { xs: "stretch", md: container === "dialog" ? "center" : undefined },
        gap: 1,
        padding: theme.spacing(2, 3),
        borderEndStartRadius: "inherit",
        borderEndEndRadius: "inherit",
        ...(dialogMode === "overflow" && {
          borderBlockStart: `1px solid ${theme.palette.border.neutral.quiet}`,
        }),
      },
      sx
    );

    return (
      <MuiDialogActions ref={ref} disableSpacing sx={styles}>
        {mainButton &&
          cloneElement(mainButton, {
            ...mainButton.props,
            variant: mainButton.props.variant || "filled",
            vibe: mainButton.props.vibe || vibe,
            type: mainButton.props.type || "submit",
          })}
        {secondaryButton &&
          cloneElement(secondaryButton, {
            ...secondaryButton.props,
            variant: secondaryButton.props.variant || "outlined",
            vibe: secondaryButton.props.vibe || "neutral",
          })}
      </MuiDialogActions>
    );
  }
);

DashboardDialogFooter.displayName = "DashboardDialogFooter";
